import {
  lerSolicitacaoPdfCertificado,
  type SolicitacaoPdfCertificadoAcessos,
} from '@/features/arbitral-detalhes';
import { throwApiError } from '@/shared/utils/apiResponse';
import { apiGet, apiPost, apiPut, downloadBlob } from '@/shared/utils/requestBuilder';
import {
  type FalhaDaRespostaDoCore,
  lerDadosDaRespostaDoCore,
} from '@/shared/utils/respostaDoCore';

import type {
  CarregarPaginaDoCertificadoInput,
  CertificadoNegociacaoResumoDto,
  ItensDasSecoesDoCertificado,
  PaginaDaSecaoDoCertificado,
  SecaoDoCertificado,
} from '../types/certificadoNegociacaoTypes';
import type {
  AssinaturaAcordoPainel,
  CancelNegociacaoStartResult,
  JanelaAtendimento,
  NegociacaoAiInsights,
  NegociacaoCasoDetail,
  NegociacaoCasoTelefoneUi,
  HistoricoDevedorResponse,
  RespondPendenciaResult,
  UpdateNegociacaoCasoInput,
  UpdateNegociacaoCasoResult,
} from '../types/negociacaoDetailTypes';
import type { NegociacaoConversa } from '../types/negociacaoDetailTypes';
import type { FetchMarcosOptions, PaginaMarcos } from '../types/negociacaoMarcoTypes';
import { paginaMarcosSchema } from '../utils/marcosSchema';

const TIMEOUT_PDF_MS = 10_000;

const ERRO_PDF_DO_CERTIFICADO = 'Erro ao gerar o PDF do Certificado da Negociação';

const ERRO_DA_EMISSAO = 'Erro ao emitir o Certificado da Negociação';

const ERRO_DA_SECAO = 'Erro ao carregar os registros do Certificado da Negociação';

/** O documento cresce de 100 em 100 registros: o contrato aceita de 1 a 200 (padrão 50). */
const LIMITE_DA_PAGINA_DE_SECAO = 100;

/**
 * Resposta não-ok da emissão ou de uma seção dela. O thunk precisa do `status` (404 vira "não
 * encontrado"), do `codigo` e dos `detalhes` (`fonte` do 503), que o `throwApiError` perderia, e
 * do `errorEventId` para o RUM achar o log do core. A mensagem é fixa: a do servidor não vai para
 * a telemetria.
 */
export class ErroDaEmissaoDoCertificado extends Error {
  readonly status: number;
  readonly codigo: string | null;
  readonly detalhes: unknown;
  readonly errorEventId: string | null;

  constructor(
    { status, codigo, detalhes, errorEventId }: FalhaDaRespostaDoCore,
    operacao: 'Emissão' | 'Seção' = 'Emissão',
  ) {
    super(`${operacao} do Certificado da Negociação respondeu HTTP ${status}`);
    this.name = 'ErroDaEmissaoDoCertificado';
    this.status = status;
    this.codigo = codigo;
    this.detalhes = detalhes;
    this.errorEventId = errorEventId;
  }
}

const unwrapBody = async <T>(response: Response): Promise<T> => {
  const body = (await response.json()) as unknown;
  const payload = (body as { data?: unknown })?.data ?? body;
  return payload as T;
};

export const negociacaoDetailService = {
  getCasoById: async (caseId: string): Promise<NegociacaoCasoDetail> => {
    const response = await apiGet(`/negociacao/casos/${caseId}`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar negociação');
    }

    const body = (await response.json()) as unknown;
    return (body as { caso: NegociacaoCasoDetail }).caso;
  },

  cancelStart: async (caseId: string): Promise<CancelNegociacaoStartResult> => {
    const response = await apiPost(`/negociacao/casos/${caseId}/cancel-start`, {});
    if (!response.ok) {
      await throwApiError(response, 'Erro ao cancelar início agendado');
    }

    const body = (await response.json()) as unknown;
    const payload = (body as { data?: unknown })?.data ?? body;
    return payload as CancelNegociacaoStartResult;
  },

  getAssinaturaAcordo: async (caseId: string): Promise<AssinaturaAcordoPainel> => {
    const response = await apiGet(`/negociacao/casos/${caseId}/assinatura`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar status de assinatura do acordo');
    }

    return unwrapBody<AssinaturaAcordoPainel>(response);
  },

  /**
   * A fila de telefones do caso. 200 com lista vazia = caso legado, sem fila — o painel cai no
   * telefone único do caso, que é o comportamento de antes desta feature.
   */
  getTelefones: async (caseId: string): Promise<NegociacaoCasoTelefoneUi[]> => {
    const response = await apiGet(`/negociacao/casos/${caseId}/telefones`);
    if (!response.ok) {
      await throwApiError(response, 'Não foi possível carregar os outros números');
    }

    const payload = await unwrapBody<{ telefones: NegociacaoCasoTelefoneUi[] }>(response);
    return payload.telefones ?? [];
  },

  getMarcos: async (caseId: string, options?: FetchMarcosOptions): Promise<PaginaMarcos> => {
    const response = await apiGet(
      `/negociacao/casos/${caseId}/marcos`,
      options?.cursor ? { cursor: options.cursor } : undefined,
      false,
      options?.signal,
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar os marcos da negociação');
    }

    const body = (await response.json()) as unknown;
    return paginaMarcosSchema.parse(body);
  },

  /**
   * A emissão vigente do certificado, só com a identificação e as contagens. O core cria uma
   * versão nova só quando o conteúdo mudou (decisão provisória 4), por isso a chamada acontece a
   * cada abertura da aba e da página e a cada acontecimento novo do caso.
   */
  obterEmissaoAtualDoCertificado: async (
    casoId: string,
  ): Promise<CertificadoNegociacaoResumoDto> => {
    const response = await apiPost(`/negociacao/casos/${casoId}/certificado/emissao-atual`);
    return lerDadosDaRespostaDoCore<CertificadoNegociacaoResumoDto>(
      response,
      ERRO_DA_EMISSAO,
      (falha) => new ErroDaEmissaoDoCertificado(falha),
    );
  },

  /**
   * Uma página de uma seção do conteúdo CONGELADO da emissão exibida: é a mesma emissão da tela e
   * do PDF, e não a mais recente. Sem cursor, a primeira página.
   */
  obterPaginaDaSecaoDoCertificado: async <S extends SecaoDoCertificado>(
    { casoId, emissaoId, cursor }: Omit<CarregarPaginaDoCertificadoInput, 'secao'>,
    secao: S,
  ): Promise<PaginaDaSecaoDoCertificado<ItensDasSecoesDoCertificado[S]>> => {
    const response = await apiGet(
      `/negociacao/casos/${casoId}/certificado/emissoes/${emissaoId}/secoes/${secao}`,
      cursor ? { cursor, limit: LIMITE_DA_PAGINA_DE_SECAO } : { limit: LIMITE_DA_PAGINA_DE_SECAO },
    );
    return lerDadosDaRespostaDoCore<PaginaDaSecaoDoCertificado<ItensDasSecoesDoCertificado[S]>>(
      response,
      ERRO_DA_SECAO,
      (falha) => new ErroDaEmissaoDoCertificado(falha, 'Seção'),
    );
  },

  /**
   * Pede o PDF da emissão EXIBIDA: o arquivo é renderizado do conteúdo congelado dela, e é o hash
   * dele que o `/autenticar` reconhece (CA-6). O `signal` de quem pediu entra junto com o timeout.
   */
  solicitarPdfDoCertificado: async (
    casoId: string,
    emissaoId: string,
    signal: AbortSignal,
  ): Promise<SolicitacaoPdfCertificadoAcessos> => {
    const response = await apiPost(
      `/negociacao/casos/${casoId}/certificado/emissoes/${emissaoId}/pdf`,
      undefined,
      false,
      AbortSignal.any([signal, AbortSignal.timeout(TIMEOUT_PDF_MS)]),
    );
    return lerSolicitacaoPdfCertificado(
      response,
      ERRO_PDF_DO_CERTIFICADO,
      'Link do Certificado da Negociação',
    );
  },

  getAiInsights: async (
    caseId: string,
    options?: { forceRefresh?: boolean },
  ): Promise<NegociacaoAiInsights | null> => {
    const response = await apiGet(
      `/negociacao/casos/${caseId}/ai-insights`,
      options?.forceRefresh ? { forceRefresh: true } : undefined,
    );

    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar insights de IA');
    }

    const body = (await response.json()) as unknown;
    return (body as { insights: NegociacaoAiInsights | null }).insights;
  },

  updateCaso: async (
    caseId: string,
    data: UpdateNegociacaoCasoInput,
  ): Promise<UpdateNegociacaoCasoResult> => {
    const response = await apiPut(`/negociacao/casos/${caseId}`, data);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao atualizar dados da negociação');
    }

    const body = (await response.json()) as unknown;
    const payload = (body as { data?: unknown })?.data ?? body;
    return payload as UpdateNegociacaoCasoResult;
  },

  downloadReport: async (caseId: string, titulo: string): Promise<void> => {
    const filename = `relatorio-negociacao-${titulo.replace(/\s+/g, '-').toLowerCase().slice(0, 40)}.pdf`;
    await downloadBlob(`/negociacao/casos/${caseId}/report`, filename);
  },

  selecionarDirecionamento: async (
    caseId: string,
    pendenciaId: string,
    selectedOptionId: string,
  ): Promise<RespondPendenciaResult> => {
    const response = await apiPost(
      `/negociacao/casos/${caseId}/pendencias/${pendenciaId}/respond`,
      { selectedOptionId },
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao registrar direcionamento');
    }
    return unwrapBody<RespondPendenciaResult>(response);
  },

  sendResumeTemplate: async (caseId: string): Promise<{ janelaAtendimento: JanelaAtendimento }> => {
    const response = await apiPost('/negociacao/messages/resume', { caseId });
    if (!response.ok) {
      await throwApiError(response, 'Não foi possível enviar a mensagem. Tente novamente.');
    }
    return unwrapBody<{ janelaAtendimento: JanelaAtendimento }>(response);
  },

  getScheduledMessagesCount: async (caseId: string): Promise<number> => {
    const response = await apiGet(`/negociacao/casos/${caseId}/scheduled-messages`, {
      status: 'PENDING',
    });
    if (!response.ok) return 0;

    const body = (await response.json()) as unknown;
    const messages = (body as { messages?: unknown[] })?.messages;
    return Array.isArray(messages) ? messages.length : 0;
  },

  getHistoricoDevedor: async (
    caseId: string,
    page: number,
    limit: number,
  ): Promise<HistoricoDevedorResponse> => {
    const response = await apiGet(`/negociacao/casos/${caseId}/historico`, { page, limit });
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar histórico do devedor');
    }

    const parsed = (await response.json()) as {
      data?: HistoricoDevedorResponse;
    } & HistoricoDevedorResponse;
    return (parsed.data ?? parsed) as HistoricoDevedorResponse;
  },

  getOrCreateConversaByCaseId: async (input: {
    empresaId: string;
    escritorioId: string;
    caseId: string;
    telefoneNormalizado: string;
  }): Promise<string | null> => {
    const params: Record<string, string | number | boolean> = {
      empresaId: input.empresaId,
      escritorioId: input.escritorioId,
      entityType: 'negociacaoCaso',
      entityId: input.caseId,
      limit: 1,
      offset: 0,
    };

    const listResponse = await apiGet('/conversas', params);

    if (!listResponse.ok) {
      await throwApiError(listResponse, 'Erro ao carregar conversa');
    }

    const listed = await unwrapBody<{ items: NegociacaoConversa[]; total: number }>(listResponse);
    const existing = listed.items?.[0] ?? null;
    if (existing) return existing.id;

    const createResponse = await apiPost('/conversas', {
      empresaId: input.empresaId,
      escritorioId: input.escritorioId,
      canal: 'WHATSAPP',
      telefoneNormalizado: input.telefoneNormalizado,
      entityType: 'negociacaoCaso',
      entityId: input.caseId,
      mode: 'BOT',
      status: 'OPEN',
    });

    if (!createResponse.ok) {
      await throwApiError(createResponse, 'Erro ao criar conversa');
    }

    const created = await unwrapBody<NegociacaoConversa>(createResponse);
    return created.id;
  },
};
